/**
 * Guest Orders API
 * 
 * Secure API wrapper for fetching guest orders via Edge Function.
 * This bypasses RLS restrictions by using service_role validation
 * against the guest_sessions table.
 */

import { APP_CONFIG } from './app-config';
import { getGuestToken, parseGuestToken } from './guestToken';

interface GuestOrdersResponse {
  orders: any[];
  error?: string;
}

/**
 * Fetch guest orders via secure Edge Function.
 * Validates guest session server-side before returning orders.
 */
export async function fetchGuestOrders(orderId?: string): Promise<any[]> {
  const token = getGuestToken();
  let sessionId: string | null = null;

  if (token) {
    const payload = parseGuestToken(token);
    sessionId = payload?.session_id || null;
  }

  // FALLBACK: iOS Safari kan rensa sessionStorage vid Swish app-switch
  // Försök hämta session_id från localStorage (sparas i PaymentPage före redirect)
  if (!sessionId) {
    sessionId = localStorage.getItem('pending_swish_guest_session_id');
    if (sessionId) {
      console.log('[guestOrdersApi] Using localStorage fallback for guest session (iOS recovery)');
    }
  }

  if (!sessionId) {
    console.log('[guestOrdersApi] No guest session found (token or localStorage)');
    return [];
  }
  
  try {
    const response = await fetch(
      `${APP_CONFIG.supabase.functionsUrl}/api-trade`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': APP_CONFIG.supabase.anonKey,
          'Authorization': `Bearer ${APP_CONFIG.supabase.anonKey}`,
        },
        body: JSON.stringify({
          path: '/guest-orders/get',
          guestSessionId: sessionId,
          orderId,
        }),
      }
    );
    
    if (!response.ok) {
      console.error('[guestOrdersApi] API error:', response.status);
      return [];
    }
    
    const data: GuestOrdersResponse = await response.json();
    
    if (data.error) {
      console.error('[guestOrdersApi] Response error:', data.error);
      return [];
    }
    
    return data.orders || [];
  } catch (err) {
    console.error('[guestOrdersApi] Fetch error:', err);
    return [];
  }
}

/**
 * Fetch a single guest order by ID.
 * Returns null if not found or access denied.
 */
export async function fetchGuestOrderById(orderId: string): Promise<any | null> {
  if (!orderId) return null;
  
  const orders = await fetchGuestOrders(orderId);
  return orders[0] || null;
}

/**
 * Check if a guest order exists and get its status.
 * Useful for polling payment status.
 */
export async function getGuestOrderStatus(orderId: string): Promise<{ status: string; qr_key: string | null } | null> {
  const order = await fetchGuestOrderById(orderId);
  if (!order) return null;
  
  return {
    status: order.status,
    qr_key: order.qr_key || null,
  };
}
